import { useState } from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Video, Users, CreditCard } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const EventsTeaser = () => {
  const { t, lang } = useLanguage();
  const [selected, setSelected] = useState(null);
  const { data: events = [] } = useQuery({
    queryKey: ["events"],
    queryFn: async () => (await api.get("/events")).data,
  });

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "pt-BR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  const handlePay = () => {
    toast(t.offers.mockToast);
    setSelected(null);
  };

  return (
    <section id="events" className="py-20 lg:py-32 bg-white" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14 max-w-xl"
        >
          <p className="font-['Ubuntu'] text-xs uppercase tracking-[0.25em] font-semibold text-[#F15BB5] mb-3">{t.events.eyebrow}</p>
          <h2 className="font-['Cutive'] text-2xl sm:text-3xl lg:text-4xl font-bold">{t.events.title}</h2>
          <p className="font-['Ubuntu'] text-black/60 mt-3">{t.events.subtitle}</p>
        </motion.div>

        {events.length === 0 ? (
          <p className="font-['Ubuntu'] text-black/50" data-testid="events-empty">{t.events.empty}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.slice(0, 3).map((ev, i) => (
              <motion.div
                key={ev.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="border-2 border-black rounded-2xl p-6 bg-[#FAFAF7]"
                data-testid={`event-card-${i}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="font-['Ubuntu'] text-xs font-bold uppercase bg-[#F15BB5] border-2 border-black rounded-full px-3 py-1">
                    {formatDate(ev.event_date)}
                  </span>
                  <span className="flex items-center gap-1 text-xs font-['Ubuntu'] text-black/60">
                    <Users className="w-3.5 h-3.5" /> {ev.spots_available} {t.events.spotsLabel}
                  </span>
                </div>
                <h3 className="font-['Ubuntu'] font-bold text-lg mb-2">{lang === "fr" ? ev.title_fr : ev.title_pt}</h3>
                <p className="font-['Ubuntu'] text-sm text-black/60 mb-5 line-clamp-2">
                  {lang === "fr" ? ev.description_fr : ev.description_pt}
                </p>
                <button
                  type="button"
                  onClick={() => setSelected(ev)}
                  className="inline-flex items-center gap-2 font-['Ubuntu'] font-bold text-sm border-2 border-black rounded-full px-4 py-2 hover:bg-black hover:text-white transition-colors"
                  data-testid={`event-register-${i}`}
                >
                  <Video className="w-4 h-4" /> {t.events.cta}
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="event-details-dialog">
          {selected && (
            <>
              <DialogHeader>
                <span className="font-['Ubuntu'] text-xs font-bold uppercase bg-[#F15BB5] border-2 border-black rounded-full px-3 py-1 self-start mb-2 inline-block">
                  {formatDate(selected.event_date)}
                </span>
                <DialogTitle className="font-['Cutive'] text-2xl">
                  {lang === "fr" ? selected.title_fr : selected.title_pt}
                </DialogTitle>
                <DialogDescription className="font-['Ubuntu'] text-sm text-black/70" data-testid="event-full-description">
                  {lang === "fr" ? selected.description_fr : selected.description_pt}
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center justify-between border-2 border-black/10 rounded-xl px-4 py-3">
                <span className="font-['Ubuntu'] text-sm text-black/60 flex items-center gap-2">
                  <Users className="w-4 h-4" /> {selected.spots_available} {t.events.spotsLabel}
                </span>
                <span className="font-['Cutive'] text-xl font-bold">{t.offers.items[1].price}</span>
              </div>
              <button
                type="button"
                onClick={handlePay}
                data-testid="event-pay-button"
                className="inline-flex items-center justify-center gap-2 bg-[#F15BB5] text-black font-['Ubuntu'] font-bold rounded-full px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#121212] hover:shadow-[2px_2px_0px_#121212] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              >
                <CreditCard className="w-4 h-4" /> {t.events.payCta}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
